import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/Button';
import { PasswordModal } from '@/components/PasswordModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { api } from '@/lib/api';
import { fetchCurrentUser } from '@/lib/auth';
import type { ApiError } from '@/lib/api';

export function ProfilePage() {
  const { user, getOAuthUrl } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState<'google' | 'microsoft' | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const handleConnectOAuth = (provider: 'google' | 'microsoft') => {
    window.location.href = getOAuthUrl(provider);
  };

  const handleDisconnectOAuth = async () => {
    if (!disconnectProvider) return;

    setIsDisconnecting(true);
    setDisconnectError(null);

    try {
      await api.post('/auth/disconnect-oauth', { provider: disconnectProvider });

      // Refresh user data
      await fetchCurrentUser();

      // Close dialog
      setDisconnectProvider(null);
    } catch (err) {
      const apiError = err as ApiError;
      setDisconnectError(apiError.message || 'Failed to disconnect account');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const isConnected = (provider: 'google' | 'microsoft') => {
    return user?.oauthAccounts?.some((oa) => oa.provider === provider) ?? false;
  };

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Profile</h1>
        <p className="text-sm text-foreground-muted">Manage your personal account information</p>
      </div>

      {/* Form */}
      <div className="max-w-2xl space-y-6">
        {/* Profile Picture */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <label className="block text-sm font-medium text-foreground mb-3">Profile Picture</label>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-xl font-medium text-primary">
              {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
            </div>
            <Button size="sm" variant="secondary">Change Photo</Button>
          </div>
        </div>

        {/* Name */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <label className="block text-sm font-medium text-foreground mb-2">Full Name</label>
          <input
            type="text"
            defaultValue={user?.name || ''}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Enter your full name"
          />
        </div>

        {/* Email */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
          <input
            type="email"
            defaultValue={user?.email || ''}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="your.email@example.com"
          />
          <p className="mt-2 text-xs text-foreground-muted">This email is used for login and notifications</p>
        </div>

        {/* Password */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <label className="block text-sm font-medium text-foreground mb-2">Password</label>

          {!user?.hasPassword ? (
            <>
              <div className="mb-3 rounded border border-warning/30 bg-warning/10 p-3">
                <p className="text-sm text-foreground">
                  <span className="font-medium">No password set.</span> You signed in using a social account.
                </p>
                <p className="mt-1 text-xs text-foreground-muted">
                  We recommend setting a password as a backup in case you ever have issues accessing your connected accounts.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setIsPasswordModalOpen(true)}>
                Set Password
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={() => setIsPasswordModalOpen(true)}>
                Change Password
              </Button>
              <p className="mt-2 text-xs text-foreground-muted">Keep your account secure by using a strong, unique password</p>
            </>
          )}
        </div>

        {/* Connected Accounts */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <label className="block text-sm font-medium text-foreground mb-3">Connected Accounts</label>
          <div className="space-y-3">
            {/* Google Account */}
            <div className="flex items-center justify-between rounded border border-border bg-background p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Google</p>
                  <p className="text-xs text-foreground-muted">
                    {isConnected('google') ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              {isConnected('google') ? (
                <Button size="sm" variant="ghost" onClick={() => setDisconnectProvider('google')}>
                  Disconnect
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => handleConnectOAuth('google')}>
                  Connect
                </Button>
              )}
            </div>

            {/* Temporarily hidden until Azure is configured */}
            {/* Microsoft Account */}
            {/* <div className="flex items-center justify-between rounded border border-border bg-background p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                  <svg className="h-6 w-6" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M0 0h11v11H0z" />
                    <path fill="#81bc06" d="M12 0h11v11H12z" />
                    <path fill="#05a6f0" d="M0 12h11v11H0z" />
                    <path fill="#ffba08" d="M12 12h11v11H12z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Microsoft</p>
                  <p className="text-xs text-foreground-muted">
                    {user?.oauthAccounts?.some((oa) => oa.provider === 'microsoft')
                      ? 'Connected'
                      : 'Not connected'}
                  </p>
                </div>
              </div>
              {user?.oauthAccounts?.some((oa) => oa.provider === 'microsoft') ? (
                <Button size="sm" variant="ghost">
                  Disconnect
                </Button>
              ) : (
                <Button size="sm" variant="secondary">
                  Connect
                </Button>
              )}
            </div> */}
          </div>
          <p className="mt-3 text-xs text-foreground-muted">
            Link your accounts to enable single sign-on across platforms
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button variant="secondary">Cancel</Button>
          <Button variant="primary">Save Changes</Button>
        </div>
      </div>

      {/* Password Modal */}
      <PasswordModal
        open={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        hasCurrentPassword={user?.hasPassword ?? false}
      />

      {/* Disconnect OAuth Confirmation */}
      <ConfirmDialog
        open={disconnectProvider !== null}
        onClose={() => {
          setDisconnectProvider(null);
          setDisconnectError(null);
        }}
        onConfirm={handleDisconnectOAuth}
        title={`Disconnect ${disconnectProvider === 'google' ? 'Google' : 'Microsoft'}?`}
        description={
          <div className="space-y-2">
            <p>
              Are you sure you want to disconnect your {disconnectProvider === 'google' ? 'Google' : 'Microsoft'} account?
            </p>
            {!user?.hasPassword && user?.oauthAccounts && user.oauthAccounts.length <= 1 && (
              <p className="text-warning">
                You don't have a password set. Please set a password before disconnecting your only login method.
              </p>
            )}
            {disconnectError && (
              <p className="text-error">{disconnectError}</p>
            )}
          </div>
        }
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="warning"
        loading={isDisconnecting}
      />
    </div>
  );
}
